import { make } from "vuex-pathify"
import { MutationTree, ActionTree, GetterTree, Module } from "vuex"
import {MultiConfirmationDialogInfo, RootState} from "@/store/RootStore"
import { StoreUtils } from "@/utils/StoreUtils"
import {AxiosError} from "axios"
import Services from "@/services/Services"
// @ts-ignore
import {Utils, Space, SpaceBooking, MultiSpaceBooking, ApiError, Workflow} from "rp-components"
import { v4 as uuidv4 } from "uuid"

const namespaced: boolean = true

//===================================
// State
//===================================
export interface CancelSpaceBookingPayload {
    spaceId: string
    bookingId: string
    propertyId: number
}

export interface CalendarDateRange {
    begin: number
    end: number
}

export interface SpacesState {
    spaces: Space[]
    space?: Space
    spaceBookings: SpaceBooking[]
    spaceBooking?: SpaceBooking
    originalSpaceBooking?: SpaceBooking,
    upcomingSpaceBookings: SpaceBooking[],
    calendarSpaceBookings: SpaceBooking[],
    calendarDateRange: CalendarDateRange,
    fetchEventsSignal: string,
    workflow?: Workflow
    spaceConflicts: string[]
}

export const state: SpacesState = {
    spaces: [],
    space: undefined,
    spaceBookings: [],
    spaceBooking: undefined,
    originalSpaceBooking: undefined,
    upcomingSpaceBookings: [],
    calendarSpaceBookings: [],
    calendarDateRange: {
        begin: new Date().getTime() - (30*24*60*60*1000),
        end: new Date().getTime() + (30*24*60*60*1000)
    },
    fetchEventsSignal: uuidv4(),
    workflow: undefined,
    spaceConflicts: []
}

export type MySpaceBooking = (SpaceBooking & { spaceName: string })

//===================================
// Getters
//===================================
export const getters: GetterTree<SpacesState, RootState> = {
    isSpaceBookingDirty({ spaceBooking, originalSpaceBooking }): boolean {
        return !Utils.isJsonEqual(spaceBooking, originalSpaceBooking)
    },

    mySpaceBookings: ({ spaces, upcomingSpaceBookings }) => (): MySpaceBooking[] => {
        return upcomingSpaceBookings
            .filter((sb) => {
                // only include bookings that correspond to spaces displayed on this page
                // (which may be shared from other properties)
                return spaces.find(s => s.spaceId === sb.spaceId) != undefined
            })
            .map((sb) => {
                const space = spaces.find(s => s.spaceId === sb.spaceId)
                return {
                    ...sb,
                    spaceName: space?.title ?? "N/A"
                }
            })
    },

    getSpaceBookingSpaceName: ({ spaces }) => (spaceId: string): string | undefined => {
        return spaces.find(s => s.spaceId === spaceId)?.title
    }
}

//===================================
// Actions
//===================================
export const actions: ActionTree<SpacesState, RootState> = {
    ...make.actions(state),

    async getSpaces({ dispatch }, propertyId: string) {
        return await StoreUtils.withLoading(dispatch, async () => {
            const spaces = await Services.spaces.getSpaces(+propertyId)
            const sortedSpaces = spaces.sort((a, b) => a.title.localeCompare(b.title))
            dispatch("setSpaces", sortedSpaces.filter((space) => !space.archived))
        })
    },

    async getSpace({ dispatch, commit }, payload: { propertyId: string; spaceId: string }) {
        return await StoreUtils.withLoading(dispatch, async () => {
            const space = await Services.spaces.getSpace(+payload.propertyId, payload.spaceId)
            dispatch("setSpace", space)

            if (space.propertyId !== parseInt(payload.propertyId)) {
                const property = await Services.property.getProperty(space.propertyId)
                commit("updateSpaceTitle", property.title)
            }
        })
    },

    async fetchBooking({ dispatch }, payload: { propertyId: number, spaceId: string; bookingId: string }): Promise<SpaceBooking | undefined> {
        let booking: SpaceBooking | undefined
        await StoreUtils.withLoading(dispatch, async () => {
            booking = await Services.spaces.getBooking(payload.propertyId, payload.spaceId, payload.bookingId)
        })
        return booking
    },

    async getSpaceBookings({ dispatch }, payload: { propertyId: number, spaceId: string }) {
        return await StoreUtils.withLoading(dispatch, async () => {
            const bookings = await Services.spaces.getSpaceBookings(
                payload.propertyId, payload.spaceId,
                state.calendarDateRange.begin, state.calendarDateRange.end
            )
            await dispatch("setSpaceBookings", bookings)
        })
    },

    async updateSpaceBooking({ dispatch }, spaceBooking: SpaceBooking) {
        const updateBooking = async (includeAssociatedBookings?: boolean, isRetry: boolean = false) => {
            const after = async () => {
                dispatch("reloadCalendarEvents")
            }
            try {
                const updatedBooking = await Services.spaces.updateSpaceBooking(spaceBooking, includeAssociatedBookings)
                dispatch("setSpaceBooking", updatedBooking)
                dispatch("setOriginalSpaceBooking", Utils.clone(updatedBooking))
                if(!isRetry) {
                    await after()
                }
            } finally {
                if(isRetry) {
                    await after()
                }
            }
        }
        return await StoreUtils.withLoading(dispatch, updateBooking, onReceived409(dispatch, updateBooking))
    },

    async cancelSpaceBooking({ dispatch }, payload: CancelSpaceBookingPayload) {
        const cancelBooking = async (includeAssociatedBookings?: boolean, isRetry: boolean = false) => {
            const after = async () => {
                dispatch("reloadCalendarEvents")
            }
            try {
                await Services.spaces.cancelSpaceBooking(payload.propertyId, payload.spaceId, payload.bookingId, includeAssociatedBookings)
                if(!isRetry) {
                    await after()
                }
            } finally {
                if(isRetry) {
                    await after()
                }
            }
        }
        return await StoreUtils.withLoading(dispatch, cancelBooking, onReceived409(dispatch, cancelBooking))
    },

    async getUpcomingSpaceBookings({ dispatch }, since: number) {
        return await StoreUtils.withLoading(dispatch, async () => {
            const upcomingSpaceBookings = (await Services.spaces.getUserSpaceBookings(since))
            dispatch("setUpcomingSpaceBookings", upcomingSpaceBookings)
        })
    },

    async getCalendarSpaceBookings({ dispatch }, payload: {propertyId: number}) {
        return await StoreUtils.withLoading(dispatch, async () => {
            const calendarSpaceBookings = await Services.spaces.getPropertySpaceBookings(payload.propertyId, state.calendarDateRange.begin, state.calendarDateRange.end)
            dispatch("setCalendarSpaceBookings", calendarSpaceBookings)
        })
    },

    async createBookingOrder({ dispatch }, bookings: MultiSpaceBooking) {
        await StoreUtils.withLoading(dispatch, async () => {
            const order = await Services.spaces.createBookingOrder(bookings)
            await dispatch("order/setOrder", order, {root: true})
        })
    },

    async checkSpaceAvailability({dispatch, state}, booking: SpaceBooking) {
        await StoreUtils.withLoading(dispatch, async() => {
            await Services.spaces.checkSpaceAvailability(booking)
            dispatch("setSpaceConflicts", state.spaceConflicts.filter(c => c !== booking.spaceId))
        }, async (err: any) => {
            const axiosError = err as AxiosError | undefined
            const apiError = axiosError?.response?.data as ApiError | undefined
            if (apiError?.errorType != "SCHEDULING_CONFLICT" && apiError?.errorType != "FORBIDDEN") {
                return false
            }
            dispatch("setSpaceConflicts", [...state.spaceConflicts, booking.spaceId])
            return true
        })
    },

    async reloadCalendarEvents({ dispatch }) {
        dispatch("setWorkflow", undefined)
        dispatch("setFetchEventsSignal", uuidv4())
    },

    async getSpaceBookingWorkflow({ dispatch }, payload: {propertyId: number, spaceId: string, bookingId: string}) {
        await StoreUtils.withLoading(dispatch, async() => {
            const workflow = await Services.spaces.getSpaceBookingWorkflow(payload.propertyId, payload.spaceId, payload.bookingId)
            dispatch("setWorkflow", workflow)
        })
    },

    resetState({ commit }) {
        commit("resetState")
    }
}

const onReceived409 = (dispatch: any, onConfirmationResponse: (includeAssociatedBookings: boolean, isRetry: boolean) => Promise<void>) => {
    return async (error: any) => {
        const axiosError = error as AxiosError | undefined
        const apiError = axiosError?.response?.data as ApiError | undefined
        if (apiError?.errorType != "CONFIRMATION_REQUIRED") {
            dispatch("reloadCalendarEvents")
            return false
        }

        const dialog: MultiConfirmationDialogInfo = {
            title: "Update Related Bookings?",
            message: apiError?.errorMessage,
            cancelTitle: "Cancel",
            firstActionTitle: "No",
            firstActionVariant: "outline-primary",
            firstAction: async () => {
                await StoreUtils.withLoading(dispatch, async () => {
                    await onConfirmationResponse(false, true)
                })
            },
            secondActionTitle: "Yes",
            secondActionVariant: "primary",
            secondAction: async () => {
                await StoreUtils.withLoading(dispatch, async () => {
                    await onConfirmationResponse(true, true)
                })
            }
        }

        await dispatch("multiConfirm", dialog, { root: true })
        return true
    }
}

//===================================
// Mutators
//===================================
const mutations: MutationTree<SpacesState> = {
    ...make.mutations(state),

    resetState(state) {
        state.spaces = []
    },

    updateSpaceTitle({ space }, sharedPropertyTitle: string) {
        if (space) {
            space.title = `${space.title} shared by ${sharedPropertyTitle}`
        }
    }
}

//===================================
// Module
//===================================
export const spaces: Module<SpacesState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
